import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, BookOpen, Search, Plus, Download, Upload, X, FileType2,
  History, Loader2, Building2, Factory, Home, Wrench, Boxes,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { Button, Card, Pill, Spinner, EmptyState } from "@/components/ui";
import {
  uploadMasterTemplate,
  getDeliverableSignedUrl,
} from "@/features/deliverables/deliverablesUpload";

// =============================================================================
// MopsLibraryScreen — /mops
// T-076 — MOPs library for external reference documents.
//
// What lives here per James 2026-04-25:
//   * CARRIER docs: NBN MOPs/STDs, Optus SOPs, Telstra commissioning STDs.
//     ~15 NBN docs to seed.
//   * SUPPLIER / MANUFACTURER docs: LeBlanc tower erection packs (~20 to
//     seed: T04T3-45, T23NB2-45, etc.), RFS antenna data sheets, plant hire
//     SOPs.
//   * INTERNAL: Coretel SOPs, rigging procedures, vehicle inspection sheets
//     — anything we author ourselves that's not project-specific.
//
// What does NOT live here:
//   * Project deliverables (Red Line Markups, As Built SID, FFC, SID, etc.)
//     → live with the project under the deliverables library.
//   * HOP build documents → live inside HOP packs.
//
// Data model: document_library_lines filtered to category code='mops'.
//   Source = exactly one of:
//     - carrier_id IS NOT NULL  → carrier-owned (NBN / Optus / Telstra)
//     - supplier_id IS NOT NULL → supplier/manufacturer-owned (LeBlanc / RFS)
//     - both NULL               → Coretel internal
//   Migration 052 adds supplier_id and the one-source CHECK constraint.
// =============================================================================

interface Carrier {
  carrier_id:   string;
  carrier_code: string;
  legal_name:   string;
}

interface Supplier {
  supplier_id:   string;
  legal_name:    string;
  trading_name:  string | null;
  supplier_kind: "general" | "structure";
}

interface LineRow {
  line_id:                    string;
  category_id:                string;
  carrier_id:                 string | null;
  supplier_id:                string | null;
  is_other:                   boolean;
  code:                       string;
  display_name:               string;
  description:                string | null;
  has_master:                 boolean;
  master_storage_path:        string | null;
  master_version_label:       string | null;
  master_uploaded_at:         string | null;
  master_file_name:           string | null;
  master_file_size_bytes:     number | null;
  master_mime_type:           string | null;
  sort_order:                 number;
  is_active:                  boolean;
  carrier:  { carrier_code: string; legal_name: string } | null;
  supplier: { legal_name: string; trading_name: string | null } | null;
}

// Source filter key: 'all' | 'internal' | 'carrier:<id>' | 'supplier:<id>'
type SourceKey = string;

function lineSourceKey(l: LineRow): SourceKey {
  if (l.carrier_id)  return `carrier:${l.carrier_id}`;
  if (l.supplier_id) return `supplier:${l.supplier_id}`;
  if (l.is_other)    return "other";
  return "internal";
}

function lineSourceLabel(l: LineRow): string {
  if (l.carrier?.carrier_code) return l.carrier.carrier_code;
  if (l.supplier) return l.supplier.trading_name ?? l.supplier.legal_name;
  if (l.is_other) return "Other";
  return "Internal";
}

// -----------------------------------------------------------------------------

export default function MopsLibraryScreen() {
  const user = useRequireAuth();
  const qc   = useQueryClient();

  const [search, setSearch]                 = useState("");
  const [sourceFilter, setSourceFilter]     = useState<SourceKey>("all");
  const [openNewDialog, setOpenNewDialog]   = useState(false);
  const [openReversion, setOpenReversion]   = useState<LineRow | null>(null);

  const isManager = user?.role === "director" || user?.role === "manager";

  // -- Find the MOPs category id (one-time lookup) ----------------------------
  const { data: mopsCategoryId } = useQuery({
    queryKey: ["mops_category_id"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("document_library_categories")
        .select("category_id")
        .eq("code", "mops")
        .single();
      if (error) throw error;
      return data?.category_id as string;
    },
  });

  const { data: carriers = [] } = useQuery({
    queryKey: ["carriers_for_mops"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("carriers")
        .select("carrier_id, carrier_code, legal_name")
        .eq("is_active", true)
        .order("carrier_code");
      if (error) throw error;
      return (data ?? []) as Carrier[];
    },
  });

  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers_for_mops"],
    queryFn: async () => {
      // Pull all active suppliers; we'll filter in the side panel to those
      // that actually have at least one MOP line, so the panel doesn't list
      // every plant hire vendor.
      const { data, error } = await supabase
        .from("suppliers")
        .select("supplier_id, legal_name, trading_name, supplier_kind")
        .order("legal_name");
      if (error) throw error;
      return (data ?? []) as Supplier[];
    },
  });

  const { data: lines = [], isLoading } = useQuery({
    enabled: !!mopsCategoryId,
    queryKey: ["mops_lines", mopsCategoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("document_library_lines")
        .select(`
          line_id, category_id, carrier_id, supplier_id, is_other, code, display_name, description,
          has_master, master_storage_path, master_version_label, master_uploaded_at,
          master_file_name, master_file_size_bytes, master_mime_type,
          sort_order, is_active,
          carrier:carriers(carrier_code, legal_name),
          supplier:suppliers(legal_name, trading_name)
        `)
        .eq("category_id", mopsCategoryId!)
        .eq("is_active", true)
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as unknown as LineRow[];
    },
  });

  // -- Counts per source for the side panel -----------------------------------
  const sourceCounts = useMemo(() => {
    const c: Record<string, number> = { all: lines.length, internal: 0, other: 0 };
    for (const l of lines) {
      const k = lineSourceKey(l);
      c[k] = (c[k] ?? 0) + 1;
    }
    return c;
  }, [lines]);

  // Structure suppliers — always shown so James can drop docs into LeBlanc / ROAM
  // without the section being empty-then-invisible until first upload.
  const structureSuppliers = useMemo(
    () => suppliers.filter(s => s.supplier_kind === "structure"),
    [suppliers],
  );

  // Generic suppliers with at least one MOP — gated, hidden until populated, so
  // the supplier directory doesn't bleed into the sidebar. Excludes structure
  // suppliers (already shown in their own section above).
  const suppliersWithMops = useMemo(() => {
    const ids = new Set<string>();
    for (const l of lines) if (l.supplier_id) ids.add(l.supplier_id);
    return suppliers.filter(s => ids.has(s.supplier_id) && s.supplier_kind !== "structure");
  }, [suppliers, lines]);

  // -- Filtered + searched ----------------------------------------------------
  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return lines.filter(l => {
      if (sourceFilter !== "all" && lineSourceKey(l) !== sourceFilter) return false;
      if (needle) {
        const hay = `${l.display_name} ${l.code} ${l.description ?? ""} ${lineSourceLabel(l)}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [lines, search, sourceFilter]);

  // -- Download master --------------------------------------------------------
  async function handleDownload(line: LineRow) {
    if (!line.master_storage_path) return;
    const url = await getDeliverableSignedUrl(
      line.master_storage_path,
      `attachment; filename="${line.master_file_name ?? line.display_name}"`,
    );
    if (url) window.open(url, "_blank");
  }

  // -- Move a MOP to a different owner (T-354) — fixes docs filed under the
  // wrong carrier/supplier (e.g. ROAM → ROCLA). Enforces the one-source rule.
  async function handleMove(line: LineRow, target: string) {
    const patch: any = { carrier_id: null, supplier_id: null, is_other: false };
    if (target.startsWith("carrier:")) patch.carrier_id = target.slice(8);
    else if (target.startsWith("supplier:")) patch.supplier_id = target.slice(9);
    else if (target === "other") patch.is_other = true;
    // "internal" → all null/false
    const { error } = await supabase.from("document_library_lines").update(patch).eq("line_id", line.line_id);
    if (error) { window.alert(`Couldn't move: ${error.message}`); return; }
    qc.invalidateQueries({ queryKey: ["mops_lines"] });
  }

  if (!user) return null;

  return (
    <div className="px-6 md:px-10 py-6 md:py-8 pb-20">
      <Link
        to="/project"
        className="inline-flex items-center gap-1.5 text-[12px] text-text-muted hover:text-accent mb-3"
      >
        <ArrowLeft size={14}/> Project portal
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div>
          <h1 className="text-[20px] font-semibold text-text flex items-center gap-2">
            <BookOpen size={20} className="text-accent" />
            MOPs
          </h1>
          <div className="text-[12px] text-text-muted mt-0.5">
            Carrier MOPs/STDs (NBN, Optus, Telstra), supplier reference docs (LeBlanc tower drawings, RFS data sheets), and Coretel internal procedures.
            {" "}{lines.length} document{lines.length === 1 ? "" : "s"} on file.
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-3 py-2 bg-ink-raised border border-line rounded-md w-72 max-w-[60vw]">
            <Search size={14} className="text-text-muted" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search MOPs…"
              className="bg-transparent border-none outline-none text-text text-[13px] flex-1"
            />
            {search && (
              <button onClick={() => setSearch("")} className="text-text-muted hover:text-text" title="Clear">
                <X size={12} />
              </button>
            )}
          </div>
          {isManager && (
            <Button icon={Plus} onClick={() => setOpenNewDialog(true)}>
              Add MOP
            </Button>
          )}
        </div>
      </div>

      {/* Two-col: source filters left, cards right */}
      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-5">
        <Card className="p-4 h-fit md:sticky md:top-3 self-start">
          <FilterRow
            label="All"
            count={sourceCounts.all ?? 0}
            active={sourceFilter === "all"}
            onClick={() => setSourceFilter("all")}
          />

          {/* Internal — own group above the externals */}
          <div className="mt-3 mb-1.5 text-[10px] uppercase tracking-wider2 text-text-muted font-mono font-semibold flex items-center gap-1.5">
            <Home size={10}/> Internal
          </div>
          <FilterRow
            label="Coretel"
            count={sourceCounts.internal ?? 0}
            active={sourceFilter === "internal"}
            onClick={() => setSourceFilter("internal")}
          />

          {/* Carriers */}
          {carriers.length > 0 && (
            <>
              <div className="mt-3 mb-1.5 text-[10px] uppercase tracking-wider2 text-text-muted font-mono font-semibold flex items-center gap-1.5">
                <Building2 size={10}/> Carriers
              </div>
              <div className="space-y-0.5">
                {carriers.map(c => (
                  <FilterRow
                    key={c.carrier_id}
                    label={c.carrier_code}
                    count={sourceCounts[`carrier:${c.carrier_id}`] ?? 0}
                    active={sourceFilter === `carrier:${c.carrier_id}`}
                    onClick={() => setSourceFilter(`carrier:${c.carrier_id}`)}
                  />
                ))}
              </div>
            </>
          )}

          {/* Structure suppliers — always shown so James can park LeBlanc / ROAM
              docs even before first upload */}
          {structureSuppliers.length > 0 && (
            <>
              <div className="mt-3 mb-1.5 text-[10px] uppercase tracking-wider2 text-text-muted font-mono font-semibold flex items-center gap-1.5">
                <Wrench size={10}/> Structure Suppliers
              </div>
              <div className="space-y-0.5">
                {structureSuppliers.map(s => (
                  <FilterRow
                    key={s.supplier_id}
                    label={s.trading_name ?? s.legal_name}
                    count={sourceCounts[`supplier:${s.supplier_id}`] ?? 0}
                    active={sourceFilter === `supplier:${s.supplier_id}`}
                    onClick={() => setSourceFilter(`supplier:${s.supplier_id}`)}
                  />
                ))}
              </div>
            </>
          )}

          {/* Suppliers (only those with at least one MOP) */}
          {suppliersWithMops.length > 0 && (
            <>
              <div className="mt-3 mb-1.5 text-[10px] uppercase tracking-wider2 text-text-muted font-mono font-semibold flex items-center gap-1.5">
                <Factory size={10}/> Suppliers
              </div>
              <div className="space-y-0.5">
                {suppliersWithMops.map(s => (
                  <FilterRow
                    key={s.supplier_id}
                    label={s.trading_name ?? s.legal_name}
                    count={sourceCounts[`supplier:${s.supplier_id}`] ?? 0}
                    active={sourceFilter === `supplier:${s.supplier_id}`}
                    onClick={() => setSourceFilter(`supplier:${s.supplier_id}`)}
                  />
                ))}
              </div>
            </>
          )}

          {/* Other — flat catch-all, always shown */}
          <div className="mt-3 mb-1.5 text-[10px] uppercase tracking-wider2 text-text-muted font-mono font-semibold flex items-center gap-1.5">
            <Boxes size={10}/> Other
          </div>
          <FilterRow
            label="Uncategorised"
            count={sourceCounts.other ?? 0}
            active={sourceFilter === "other"}
            onClick={() => setSourceFilter("other")}
          />

          <div className="mt-4 pt-3 border-t border-line text-[10px] text-text-dim leading-snug">
            Project deliverables (Red Line Markups, As Built SID, FFC, SID, etc.) live with the project, not here.
          </div>
        </Card>

        {/* Right grid */}
        <div>
          {isLoading ? (
            <div className="flex items-center justify-center py-16"><Spinner /></div>
          ) : visible.length === 0 ? (
            <EmptyState
              icon={BookOpen}
              title="No MOPs match"
              description={search
                ? `Nothing matches "${search}". Try a different filter or term.`
                : isManager
                  ? "No MOPs uploaded yet. Click \"Add MOP\" to seed the library — start with the LeBlanc erection packs and the NBN STDs you've already got."
                  : "No MOPs in this view yet. Ask a manager to upload one."}
            />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {visible.map(line => (
                <MopCard
                  key={line.line_id}
                  line={line}
                  isManager={isManager}
                  carriers={carriers}
                  suppliers={suppliers}
                  onDownload={() => handleDownload(line)}
                  onReversion={() => setOpenReversion(line)}
                  onMove={(target) => handleMove(line, target)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Dialogs */}
      {openNewDialog && mopsCategoryId && (
        <NewMopDialog
          mopsCategoryId={mopsCategoryId}
          carriers={carriers}
          suppliers={suppliers}
          onClose={() => setOpenNewDialog(false)}
          onSaved={() => {
            setOpenNewDialog(false);
            qc.invalidateQueries({ queryKey: ["mops_lines"] });
          }}
        />
      )}
      {openReversion && (
        <ReversionDialog
          line={openReversion}
          onClose={() => setOpenReversion(null)}
          onSaved={() => {
            setOpenReversion(null);
            qc.invalidateQueries({ queryKey: ["mops_lines"] });
          }}
        />
      )}
    </div>
  );
}

// -----------------------------------------------------------------------------
// FilterRow — side filter button with count
// -----------------------------------------------------------------------------
function FilterRow({ label, count, active, onClick }: {
  label: string; count: number; active: boolean; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded text-[12px] transition-colors ${
        active
          ? "bg-accent/12 text-accent font-semibold"
          : "text-text hover:bg-ink-hover"
      }`}
    >
      <span className="truncate text-left">{label}</span>
      <span className={`font-mono text-[10px] ${active ? "text-accent" : "text-text-muted"}`}>{count}</span>
    </button>
  );
}

// -----------------------------------------------------------------------------
// MopCard
// -----------------------------------------------------------------------------
function MopCard({ line, isManager, carriers, suppliers, onDownload, onReversion, onMove }: {
  line: LineRow;
  isManager: boolean;
  carriers: any[];
  suppliers: any[];
  onDownload: () => void;
  onReversion: () => void;
  onMove: (target: string) => void;
}) {
  const sourceLabel = lineSourceLabel(line);
  // Pill tone: carrier=notice (blue-ish), supplier=quoted (accent), internal=default
  const tone: "notice" | "quoted" | "default" = line.carrier_id
    ? "notice"
    : line.supplier_id
      ? "quoted"
      : "default";

  return (
    <Card className="p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold text-text leading-snug" title={line.display_name}>
            {line.display_name}
          </div>
          <div className="text-[10px] font-mono text-text-muted mt-0.5">{line.code}</div>
        </div>
        <FileType2 size={18} className="text-text-muted flex-shrink-0" />
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        <Pill label={sourceLabel} tone={tone} small />
      </div>

      {line.description && (
        <div className="text-[11px] text-text-muted line-clamp-3" title={line.description}>
          {line.description}
        </div>
      )}

      <div className="mt-auto pt-2 border-t border-line">
        {line.has_master && line.master_storage_path ? (
          <>
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-[10px] text-text-muted font-mono truncate" title={line.master_file_name ?? ""}>
                <span className="text-text-dim">v</span>{line.master_version_label ?? "—"}
                {line.master_uploaded_at && (
                  <span className="text-text-dim"> · {new Date(line.master_uploaded_at).toLocaleDateString()}</span>
                )}
              </div>
              {line.master_file_size_bytes != null && (
                <div className="text-[10px] font-mono text-text-dim flex-shrink-0">
                  {fmtSize(line.master_file_size_bytes)}
                </div>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <Button size="sm" icon={Download} onClick={onDownload} className="flex-1">
                Download
              </Button>
              {isManager && (
                <Button size="sm" variant="secondary" icon={History} onClick={onReversion} title="Replace with new version">
                  Replace
                </Button>
              )}
            </div>
            {/* T-354 — move to a different owner (fixes mis-filed docs) */}
            {isManager && (
              <select
                value={lineSourceKey(line)}
                onChange={(e) => onMove(e.target.value)}
                title="Move this MOP to a different owner"
                className="mt-2 w-full bg-ink border border-line rounded px-2 py-1 text-[11px] text-text-muted"
              >
                <option value="internal">Move to: Internal — Coretel</option>
                <optgroup label="Carriers">
                  {carriers.map((c) => (
                    <option key={c.carrier_id} value={`carrier:${c.carrier_id}`}>{c.carrier_code} — {c.legal_name}</option>
                  ))}
                </optgroup>
                <optgroup label="Suppliers">
                  {suppliers.map((s) => (
                    <option key={s.supplier_id} value={`supplier:${s.supplier_id}`}>{s.trading_name ?? s.legal_name}</option>
                  ))}
                </optgroup>
                <option value="other">Move to: Other / uncategorised</option>
              </select>
            )}
          </>
        ) : (
          <div className="flex items-center justify-between gap-2">
            <div className="text-[10px] italic text-text-dim">No file uploaded yet</div>
            {isManager && (
              <Button size="sm" variant="secondary" icon={Upload} onClick={onReversion}>
                Upload
              </Button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

// -----------------------------------------------------------------------------
// NewMopDialog — manager+ adds a new MOP entry + uploads first version
// -----------------------------------------------------------------------------
function NewMopDialog({ mopsCategoryId, carriers, suppliers, onClose, onSaved }: {
  mopsCategoryId: string;
  carriers:       Carrier[];
  suppliers:      Supplier[];
  onClose: () => void;
  onSaved: () => void;
}) {
  // sourceMode controls which FK / flag gets set. UI radio: internal / carrier / supplier / other
  const [sourceMode, setSourceMode] = useState<"internal" | "carrier" | "supplier" | "other">("carrier");
  const [carrierId, setCarrierId]   = useState<string>(carriers[0]?.carrier_id ?? "");
  const [supplierId, setSupplierId] = useState<string>(suppliers[0]?.supplier_id ?? "");
  const [displayName, setDisplayName] = useState("");
  const [code, setCode]             = useState("");
  const [description, setDescription] = useState("");
  const [versionLabel, setVersionLabel] = useState("v1.0");
  const [file, setFile]             = useState<File | null>(null);
  const [busy, setBusy]             = useState(false);
  const [err, setErr]               = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  function handleNameChange(v: string) {
    setDisplayName(v);
    if (!code || code === slugify(displayName)) {
      setCode(slugify(v));
    }
  }

  async function handleSave() {
    if (!displayName.trim() || !code.trim()) {
      setErr("Document name and code are required.");
      return;
    }
    if (sourceMode === "carrier"  && !carrierId)  { setErr("Pick a carrier."); return; }
    if (sourceMode === "supplier" && !supplierId) { setErr("Pick a supplier."); return; }
    setBusy(true);
    setErr(null);
    try {
      const { data: created, error } = await supabase
        .from("document_library_lines")
        .insert({
          category_id:  mopsCategoryId,
          carrier_id:   sourceMode === "carrier"  ? carrierId  : null,
          supplier_id:  sourceMode === "supplier" ? supplierId : null,
          is_other:     sourceMode === "other",
          code:         code.trim(),
          display_name: displayName.trim(),
          description:  description.trim() || null,
          has_master:   !!file,
          sort_order:   100,
        })
        .select("line_id")
        .single();
      if (error) throw error;

      if (file && created?.line_id) {
        await uploadMasterTemplate({
          lineId:       created.line_id,
          file,
          versionLabel: versionLabel.trim() || null,
        });
      }
      onSaved();
    } catch (e: any) {
      setErr(e?.message ?? "Failed to add MOP.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <DialogShell title="Add MOP" onClose={onClose} busy={busy}>
      {err && (
        <div className="mb-3 px-3 py-2 bg-danger/12 border border-danger/30 rounded text-[12px] text-danger">{err}</div>
      )}

      <Field label="Source">
        <div className="grid grid-cols-4 gap-1">
          <SourceModeChip label="Internal" icon={Home}      active={sourceMode === "internal"} onClick={() => setSourceMode("internal")}/>
          <SourceModeChip label="Carrier"  icon={Building2} active={sourceMode === "carrier"}  onClick={() => setSourceMode("carrier")} />
          <SourceModeChip label="Supplier" icon={Factory}   active={sourceMode === "supplier"} onClick={() => setSourceMode("supplier")}/>
          <SourceModeChip label="Other"    icon={Boxes}     active={sourceMode === "other"}    onClick={() => setSourceMode("other")}   />
        </div>
      </Field>

      {sourceMode === "carrier" && (
        <Field label="Which carrier?">
          <select value={carrierId} onChange={(e) => setCarrierId(e.target.value)} className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text">
            {carriers.map(c => <option key={c.carrier_id} value={c.carrier_id}>{c.carrier_code} — {c.legal_name}</option>)}
          </select>
        </Field>
      )}
      {sourceMode === "supplier" && (
        <Field label="Which supplier / manufacturer?">
          <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text">
            {suppliers.length === 0 && <option value="">(no suppliers loaded — add one first)</option>}
            {suppliers.map(s => <option key={s.supplier_id} value={s.supplier_id}>{s.trading_name ?? s.legal_name}</option>)}
          </select>
        </Field>
      )}

      <Field label="Document name">
        <input
          value={displayName}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder={
            sourceMode === "supplier" ? "e.g. T04T3-45 Type 3 45m Erection Drawings"
            : sourceMode === "carrier" ? "e.g. NBN-DEP-STD-4271 Wireless Standard Drawing Pack"
            : "e.g. Coretel rigging procedure"
          }
          className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"
        />
      </Field>
      <Field label="Code (short identifier)">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="t04t3_45 / nbn_dep_std_4271 / rigging_sop"
          className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text font-mono"
        />
      </Field>
      <Field label="Description (optional)">
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          placeholder="What is this MOP for? When do crews use it?"
          className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"
        />
      </Field>
      <Field label="Version label">
        <input
          value={versionLabel}
          onChange={(e) => setVersionLabel(e.target.value)}
          className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"
        />
      </Field>
      <Field label="File (PDF, xlsx, docx, etc.)">
        <input
          ref={fileRef}
          type="file"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="w-full text-[12px] text-text"
        />
        {file && <div className="text-[11px] text-text-muted mt-1">{file.name} · {fmtSize(file.size)}</div>}
      </Field>

      <div className="flex items-center justify-end gap-2 pt-3 border-t border-line">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={handleSave} disabled={busy} icon={busy ? Loader2 : Plus}>
          {busy ? "Saving…" : "Add MOP"}
        </Button>
      </div>
    </DialogShell>
  );
}

function SourceModeChip({ label, icon: Icon, active, onClick }: {
  label: string; icon: typeof Home; active: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[12px] border transition-colors ${
        active
          ? "bg-accent/12 text-accent border-accent/40 font-semibold"
          : "bg-ink border-line text-text-muted hover:text-text hover:border-line-light"
      }`}
    >
      <Icon size={12}/>
      {label}
    </button>
  );
}

// -----------------------------------------------------------------------------
// ReversionDialog — manager+ replaces / uploads new version
// -----------------------------------------------------------------------------
function ReversionDialog({ line, onClose, onSaved }: {
  line: LineRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [versionLabel, setVersionLabel] = useState(
    line.master_version_label ? bumpVersion(line.master_version_label) : "v1.0"
  );
  const [archiveNotes, setArchiveNotes] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr]   = useState<string | null>(null);

  const isReplacement = !!line.master_storage_path;

  async function handleSave() {
    if (!file) {
      setErr("Pick a file to upload.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await uploadMasterTemplate({
        lineId:       line.line_id,
        file,
        versionLabel: versionLabel.trim() || null,
        archiveNotes: archiveNotes.trim() || null,
      });
      onSaved();
    } catch (e: any) {
      setErr(e?.message ?? "Upload failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <DialogShell
      title={isReplacement ? `Replace — ${line.display_name}` : `Upload — ${line.display_name}`}
      onClose={onClose}
      busy={busy}
    >
      {err && (
        <div className="mb-3 px-3 py-2 bg-danger/12 border border-danger/30 rounded text-[12px] text-danger">{err}</div>
      )}
      {isReplacement && (
        <div className="mb-3 p-2.5 bg-warn/10 border border-warn/25 rounded text-[12px] text-warn">
          Current version <span className="font-mono">{line.master_version_label ?? "—"}</span> will be archived to history.
        </div>
      )}
      <Field label="New version label">
        <input
          value={versionLabel}
          onChange={(e) => setVersionLabel(e.target.value)}
          className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"
        />
      </Field>
      <Field label="File">
        <input
          type="file"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="w-full text-[12px] text-text"
        />
        {file && <div className="text-[11px] text-text-muted mt-1">{file.name} · {fmtSize(file.size)}</div>}
      </Field>
      {isReplacement && (
        <Field label="Why are you replacing it? (optional)">
          <textarea
            value={archiveNotes}
            onChange={(e) => setArchiveNotes(e.target.value)}
            rows={2}
            placeholder="Reason — carrier issued a new version, internal revision, etc."
            className="w-full bg-ink border border-line rounded px-2.5 py-1.5 text-[13px] text-text"
          />
        </Field>
      )}

      <div className="flex items-center justify-end gap-2 pt-3 border-t border-line">
        <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button onClick={handleSave} disabled={busy} icon={busy ? Loader2 : Upload}>
          {busy ? "Uploading…" : (isReplacement ? "Replace version" : "Upload")}
        </Button>
      </div>
    </DialogShell>
  );
}

// -----------------------------------------------------------------------------
// Generic dialog shell + Field
// -----------------------------------------------------------------------------
function DialogShell({ title, busy, children, onClose }: {
  title: string; busy: boolean; children: React.ReactNode; onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center pt-16 px-4 overflow-y-auto" onClick={busy ? undefined : onClose}>
      <div
        className="bg-ink-card border border-line rounded-lg shadow-xl w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="text-[15px] font-semibold text-text">{title}</div>
          <button onClick={onClose} disabled={busy} className="p-1 rounded hover:bg-ink-hover text-text-muted disabled:opacity-50">
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3">
      <label className="block text-[11px] font-mono uppercase tracking-wider2 text-text-muted mb-1">{label}</label>
      {children}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Helpers
// -----------------------------------------------------------------------------
function slugify(s: string): string {
  return s.toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 60);
}

function bumpVersion(v: string): string {
  const m = v.match(/^v(\d+)\.(\d+)$/i);
  if (m) return `v${m[1]}.${parseInt(m[2], 10) + 1}`;
  return `${v} (new)`;
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
